import BookingSummaryCard from "./BookingSummaryCard";
import SwipeableViews from "react-swipeable-views";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CarouselArrows from "../CarouselArrows";
import { setSelectedBooking } from "../../../../../redux/slice/booking/bookingSlice";
import { BookingSummary as BookingSummaryProps } from "../../../../../types/bookingCreation";
import { RootState } from "../../../../../redux/store";

export type BookingGroup = {
  id: string;
  service: string;
  boards: BookingSummaryProps[];
};

interface Props {
  userReservation: BookingGroup[];
  serviceType: string;
}

function BookingSummary(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedBooking = useSelector(
    (state: RootState) => state.booking.selectedBooking
  );

  // D4: each carousel slide represents one bookingNumber group. Boards
  // inside a SUP / equipmentRental multi-board booking render as an
  // Apple-Wallet-style accordion inside the slide: every board is shown
  // in alphabetical order; non-focused boards collapse to a fixed peek
  // strip via overflow-hidden + max-height; the focused board expands to
  // its natural height. KEYS pickup + return stays per-slide per scope
  // guard upstream.
  const focusedGroupIndex = useMemo(() => {
    if (!selectedBooking) return 0;
    const idx = props.userReservation.findIndex((group) =>
      group.boards.some((board) => board.id === selectedBooking.id)
    );
    return idx >= 0 ? idx : 0;
  }, [selectedBooking, props.userReservation]);

  const [activeStep, setActiveStep] = useState(focusedGroupIndex);

  useEffect(() => {
    // Auto-select the first board of the first group when there's no current
    // selection (first render) OR when the current selection is no longer in
    // the list (after a service-type switch, `selectedBooking` still points at
    // the previous service's order while the carousel already shows the new
    // one). Keeping the global selection in sync with the displayed list is
    // what the card CTA relies on. The `found` check is against the same
    // `userReservation` we select from, so the newly-selected board is always
    // present and this converges in one pass (no update loop).
    if (props.userReservation.length === 0) return;
    const found =
      !!selectedBooking &&
      props.userReservation.some((group) =>
        group.boards.some((board) => board.id === selectedBooking.id)
      );
    if (!found) {
      const firstBoard = props.userReservation[0]?.boards[0];
      if (firstBoard) {
        dispatch(setSelectedBooking(firstBoard));
      }
    }
  }, [props.userReservation, dispatch, selectedBooking]);

  // `focusedGroupIndex` is the single source of truth for the active
  // slide. On a service-type switch `props.userReservation` changes, the
  // stale `selectedBooking` is no longer found in the new list, so
  // `focusedGroupIndex` recomputes to 0 and this effect resets the
  // carousel — no separate `setActiveStep(0)` reset effect needed (which
  // previously raced this one). See #1035 item 10.
  useEffect(() => {
    setActiveStep(focusedGroupIndex);
  }, [focusedGroupIndex]);

  const focusBoard = (board: BookingSummaryProps) => {
    dispatch(setSelectedBooking(board));
  };

  const handleChangeIndex = (index: number) => {
    setActiveStep(index);
    const group = props.userReservation[index];
    if (group?.boards[0]) {
      focusBoard(group.boards[0]);
    }
  };

  return (
    <div className="flex flex-row overflow-hidden relative">
      {/* The wrapper hugs the carousel's 380px box so the desktop arrows
          anchor to its edges, not to the full-width page row. */}
      <div className="relative">
        <SwipeableViews
        axis="x"
        index={activeStep}
        onChangeIndex={handleChangeIndex}
        enableMouseEvents
        className="px-5 w-[380px] overflow-visible no-scrollbar my-5"
        style={{
          padding: "0 40px",
        }}
      >
        {props.userReservation?.map((group, index) => {
          const isMultiBoard = group.boards.length > 1;
          const focusedBoardId = group.boards.some(
            (b) => b.id === selectedBooking?.id
          )
            ? selectedBooking!.id
            : group.boards[0].id;
          const focusedBoard =
            group.boards.find((b) => b.id === focusedBoardId) ??
            group.boards[0];
          // `group.boards` is already alphabetically sorted by the
          // upstream `differentiateSameBookings`, so iterating in order
          // yields a natural A → B → C reading regardless of focus.
          // PEEK_HEIGHT_PX is the height the collapsed peek rows expose
          // — enough to show the Board-letter pill plus the status pill
          // on the right of each card.
          const PEEK_HEIGHT_PX = 48;
          // Board letter is the last char of the *displayed*
          // bookingNumber (e.g. "S-00000013A" → "A"). Single-board
          // bookings keep the bare bookingNumber and fall through to
          // the empty-letter branch (no badge).
          const letterFor = (board: BookingSummaryProps) => {
            const last = board.bookingNumber?.slice(-1) ?? "";
            return /^[A-Z]$/.test(last) ? last : "";
          };
          const renderCard = (board: BookingSummaryProps) => {
            const letter = letterFor(board);
            return (
              <div className="relative">
                {isMultiBoard && letter && (
                  <span
                    className="absolute top-2 left-2 z-10 inline-flex items-center px-3 py-1 rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow-sm"
                    aria-label={`Board ${letter}`}
                  >
                    Board {letter}
                  </span>
                )}
                <BookingSummaryCard
                  serviceType={props.serviceType}
                  bookingSummaryData={board}
                  amount={1}
                />
              </div>
            );
          };
          return (
            <div
              key={group.id}
              className={`w-[280px] ${
                index === activeStep
                  ? "blur-none opacity-100"
                  : "blur-[1px] opacity-98"
              }`}
            >
              {!isMultiBoard ? (
                renderCard(focusedBoard)
              ) : (
                <div className="flex flex-col gap-2">
                  {/* Render boards in REVERSE alphabetical order: C top,
                      B middle, A bottom. Default-focused board (the
                      alphabetically first, A) therefore sits at the
                      bottom of the visual stack with the others peeking
                      above it — matching the PO design preference. */}
                  {[...group.boards].reverse().map((board) => {
                    const isThisFocused = board.id === focusedBoardId;
                    return (
                      <div
                        key={board.id}
                        onClick={() =>
                          !isThisFocused && focusBoard(board)
                        }
                        onKeyDown={
                          isThisFocused
                            ? undefined
                            : (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  focusBoard(board);
                                }
                              }
                        }
                        role={isThisFocused ? undefined : "button"}
                        tabIndex={isThisFocused ? undefined : 0}
                        aria-pressed={isThisFocused ? undefined : false}
                        className={`overflow-hidden transition-[max-height] duration-200 ease-out ${
                          isThisFocused
                            ? ""
                            : "cursor-pointer hover:opacity-95"
                        }`}
                        style={{
                          maxHeight: isThisFocused
                            ? "none"
                            : `${PEEK_HEIGHT_PX}px`,
                        }}
                      >
                        {renderCard(board)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        </SwipeableViews>
        <CarouselArrows
          activeStep={activeStep}
          count={props.userReservation.length}
          onStepChange={handleChangeIndex}
          previousLabel={t("orders.carousel_previous")}
          nextLabel={t("orders.carousel_next")}
        />
      </div>
    </div>
  );
}

export default BookingSummary;
